import { z } from 'zod';
import { IntakeClientWorkspaceLinkSchema } from './intakeClientWorkspace.js';
import { INTAKE_SNAPSHOT_MAX_ACCOUNTS, IntakeDraftSnapshotSchema, IntakeSnapshotAccountSchema } from './intakeSnapshot.js';
import {
  isDebtAccountType, isDocumentRefDisclosable, snapshotAccountIds, snapshotDisclosableAccountIds,
} from './intakeDebtAccounts.js';
import { IntakeDraftDocumentRefSchema, INTAKE_STATEMENT_MAX_PER_DRAFT } from './intakeStatements.js';

/**
 * What a firm is sent of a SUBMITTED intake's nested stored records (crossmarc/steward-app#1836,
 * files from crossmarc/steward-app#2309).
 *
 * The stored `advisor-intake` row carries the whole account-link snapshot, the document
 * references copied at submit and the whole sponsored-setup link. These schemas name the fields
 * kept from each (built from the stored schemas' own field definitions, so a kept field keeps its
 * stored format), and none is `strictObject`, so a stored field that is not named here is stripped on
 * parse rather than refused. The façade (`toIntakeResource`, `handlers/advisor.ts`) first parses
 * the stored value with the STORED schema and only then reduces it through these, so a value the
 * stored schema refuses is never shown. The firm SPA's readers (`readFirmSnapshot`,
 * `readFirmDocuments`, `intakeClientWorkspaceLink` in `src/advisor/`) parse the response through
 * the same schemas.
 *
 * "Read by a firm screen or the PDF" is a reading of `src/advisor/` and `intakePdf.ts` at
 * origin/main 2e25af30 (2026-10-06), not an invariant. What is pinned: the façade's output key sets
 * (`advisor.test.ts`, the `#1836` cases) and each schema's key list (`intakeFirmView.test.ts`). The
 * SPA's type for those attributes is loose (`snapshot` and `clientWorkspace` are `unknown`,
 * `documents` is `unknown[]`), so a read of a dropped field fails
 * typecheck only when it goes through one of the readers; `AdvisorIntakes.tsx` also tests
 * `clientWorkspace !== undefined` directly, which is why an unreadable stored link is sent as `{}`.
 *
 * ⚠️ **Deployed versions can disagree about this shape.** The API deploys before the SPA, and a
 * bundle cached by the service worker outlives that. An SPA bundle older than this change parses
 * the snapshot with the full strict schema and the link with the full link schema, so reading the
 * projected shape it shows no connected accounts and no setup link until it takes the new bundle (the PWA registers with `registerType: 'prompt'`,
 * `vite.config.ts`, so a plain reload may not do that). No
 * compatibility code was added for that window.
 */

const account = IntakeSnapshotAccountSchema.shape;

/**
 * The mask to send a firm for one stored account, or `undefined` for none: a string that is not
 * empty and that the stored schema's own `mask` rule accepts (`IntakeSnapshotAccountSchema.mask`,
 * read 2026-10-07: at most 8 letters and digits). There is no separate length bound here. A
 * non-string, an empty string, or a string the stored rule refuses (a space, a hyphen, nine
 * characters) yields `undefined`, so the firm is never sent a value the stored schema would not
 * have stored. Both façade projections (draft and submitted) call this, so they cannot disagree.
 */
export function firmAccountMask(mask: unknown): string | undefined {
  return typeof mask === 'string' && mask.length >= 1 && account.mask.safeParse(mask).success ? mask : undefined;
}

/**
 * One connected account as the firm is sent it (crossmarc/steward-app#2152, founder decision
 * 2026-10-06): the six fields a firm screen reads plus `mask`, the last characters the bank
 * itself displays, so an advisor can tell two similar accounts apart. Not sent: `accountId`,
 * `available`, `statementsUnavailable`. `intakeFirmView.test.ts` enumerates the STORED account
 * schema's keys and asserts that every key outside this list is withheld, so a field added to the
 * stored schema is withheld until it is added here on purpose.
 *
 * `mask` (2026-10-07, founder decision recorded on crossmarc/steward-app#2165): the firm is sent
 * the stored mask at any length the stored schema admits ({@link firmAccountMask}, the one place
 * both façade projections decide it). The stored schema allows at most 8 letters and digits
 * (`IntakeSnapshotAccountSchema.mask`, read 2026-10-07) and allows the empty string, which is
 * sent as no mask. The founder approved showing firms whole account numbers. On the honest path
 * the value is Plaid's mask, what the bank itself displays: the full number is not requested from
 * Plaid (the `auth` product is not in the link token, `intakeSnapshot.ts`). The snapshot is echoed from the
 * prospect's browser, so a session holder can store any value the stored schema admits. This
 * replaces the 4-character limit #2152 first shipped. The firm SCHEMA below is the stored field
 * unchanged, so it refuses nothing the stored schema accepts (a stricter consumer would discard a
 * whole snapshot over one value), and the firm screens treat an empty one as none. A surface that
 * shows it renders it as text.
 *
 * A pulled statement's file name, which a firm already sees, carries the mask too: it is built as
 * "<institution> <account> (<mask>) statement YYYY-MM.pdf" (`statementFilename`,
 * `services/intake-reader/src/plaidSnapshot.ts`, read 2026-10-07), with institution and mask each
 * optional in the name, from a statements-list mask of 1 to 8 letters and digits (same file).
 */
export const INTAKE_FIRM_ACCOUNT_KEYS = ['institution', 'name', 'mask', 'type', 'subtype', 'currency', 'balance'] as const;
export const IntakeFirmSnapshotAccountSchema = z.object({
  institution: account.institution,
  name: account.name,
  mask: account.mask,
  type: account.type,
  subtype: account.subtype,
  currency: account.currency,
  balance: account.balance,
});
export type IntakeFirmSnapshotAccount = z.infer<typeof IntakeFirmSnapshotAccountSchema>;

/** The snapshot as the firm is sent it: the accounts and nothing beside them. Not sent: `asOf`,
 *  `provider`, `institutions`, `holdings`, `holdingsTruncated`. */
export const INTAKE_FIRM_SNAPSHOT_KEYS = ['accounts'] as const;
export const IntakeFirmSnapshotSchema = z.object({
  accounts: z.array(IntakeFirmSnapshotAccountSchema).max(INTAKE_SNAPSHOT_MAX_ACCOUNTS),
});
export type IntakeFirmSnapshot = z.infer<typeof IntakeFirmSnapshotSchema>;

const documentRef = IntakeDraftDocumentRefSchema.shape;

/**
 * One file as the firm is sent it (crossmarc/steward-app#2309): a REFERENCE to a client document,
 * for grouping and for naming. It authorises nothing. A download goes through the client
 * document route (`handlers/firmClientDocuments.ts`), which checks the team and that the row is
 * this client's own; the SPA offers it only for an id the client's own document list holds.
 *
 * Not sent: `accountId` (crossmarc/steward-app#2185, the firm gets the account's POSITION in its
 * own account list instead), `contentType` and `size` (the client's document row has them).
 *
 * `uploadedAt` IS sent (fix round 1): the Requests tab dates a request's answer from it, so an
 * answer does not turn back into "unanswered" when the firm deletes the document, when the caller
 * is off the client's team, or while the list loads. It is set by server code on both paths, from
 * appcore's own `createdAt` of the committed document (`services/intake-reader/src/index.ts` for
 * a browser upload, `services/kept-link/src/statementStore.ts` for a bank statement). The commit
 * request a browser makes carries only the upload id, name and type (`handlers/intakeDraft.ts`).
 * Read 2026-10-13.
 * At most one of `rowId` (the id the client's own typed row carries) and `accountIndex` (the
 * position of the connected account in the firm's own account list) is sent.
 */
export const INTAKE_FIRM_DOCUMENT_KEYS = ['documentId', 'name', 'section', 'source', 'uploadedAt', 'rowId', 'accountIndex'] as const;
export const IntakeFirmDocumentSchema = z.object({
  documentId: documentRef.documentId,
  name: documentRef.name,
  section: documentRef.section,
  source: documentRef.source,
  uploadedAt: documentRef.uploadedAt,
  rowId: documentRef.rowId,
  accountIndex: z.number().int().min(0).max(INTAKE_SNAPSHOT_MAX_ACCOUNTS).optional(),
}).refine((file) => file.rowId === undefined || file.accountIndex === undefined, {
  message: 'A file sits under a typed row or under a connected account, not both.',
  path: ['rowId'],
});
export type IntakeFirmDocument = z.infer<typeof IntakeFirmDocumentSchema>;

/**
 * The `accountId` of each account the firm is sent, in the order it is sent: the non-debt
 * accounts of a snapshot that parses as a whole, empty otherwise. This is the list
 * `IntakeFirmSnapshotSchema.accounts` is built from, so an index into it indexes the firm's own
 * `accounts` array.
 */
export function firmAccountIdsInOrder(storedSnapshot: unknown): string[] {
  const strict = IntakeDraftSnapshotSchema.safeParse(storedSnapshot);
  if (!strict.success) return [];
  return strict.data.accounts.filter(account => !isDebtAccountType(account.type)).map(account => account.accountId);
}

/**
 * The files a firm is sent for ONE row (a draft or a submitted intake), from the row's stored
 * `documents` and its stored `snapshot`. The only place the #2150 rule is applied to a
 * reference, and every firm-side count and name list (the intake's files, the draft's count, its
 * names, its sections started) is taken from what this returns, so they cannot disagree.
 *
 *   - An entry that does not pass `IntakeDraftDocumentRefSchema` is not sent and not counted.
 *     A stored value that is not an array is no files.
 *   - A second entry with the same `documentId` is dropped, whatever it says: the first entry
 *     that passes the schema claims the id, before the disclosure rule and before the cap, so a
 *     repeated id is neither sent twice nor counted twice nor able to fill the cap.
 *   - An entry {@link isDocumentRefDisclosable} refuses is not sent and not counted.
 *   - The rest are reduced to {@link IntakeFirmDocumentSchema}, each field named. `accountIndex`
 *     is set only when the entry's account is in the firm's own list; an orphan (`'client'`
 *     source, account in no snapshot entry) is sent with neither tie.
 *
 * Reads raw stored values and never throws. At most {@link INTAKE_STATEMENT_MAX_PER_DRAFT}
 * files, the writer's own cap, so a hand-written row cannot make the response unbounded.
 */
export function firmDocumentsOf(storedDocuments: unknown, storedSnapshot: unknown): IntakeFirmDocument[] {
  if (!Array.isArray(storedDocuments)) return [];
  const disclosable = snapshotDisclosableAccountIds(storedSnapshot);
  const present = snapshotAccountIds(storedSnapshot);
  const order = firmAccountIdsInOrder(storedSnapshot);
  const out: IntakeFirmDocument[] = [];
  const seen = new Set<string>();
  for (const entry of storedDocuments) {
    if (out.length >= INTAKE_STATEMENT_MAX_PER_DRAFT) break;
    const ref = IntakeDraftDocumentRefSchema.safeParse(entry);
    if (!ref.success) continue;
    const { documentId, name, section, source, uploadedAt, rowId, accountId } = ref.data;
    if (seen.has(documentId)) continue;
    seen.add(documentId);
    if (!isDocumentRefDisclosable({ source, accountId }, disclosable, present)) continue;
    const index = accountId === undefined ? -1 : order.indexOf(accountId);
    out.push(IntakeFirmDocumentSchema.parse({
      documentId, name, section, source, uploadedAt,
      ...(rowId === undefined ? {} : { rowId }),
      ...(index === -1 ? {} : { accountIndex: index }),
    }));
  }
  return out;
}

const link = IntakeClientWorkspaceLinkSchema.shape;

/** The sponsored-setup link as the firm is sent it: which workspace and which state. Not sent:
 *  `tenantId`, `linkedAt`, `linkedBy`, `lastInvitedAt`, `cancelled*`, `ended*`, `prefill`. */
export const INTAKE_FIRM_CLIENT_WORKSPACE_KEYS = ['workspaceId', 'state'] as const;
export const IntakeFirmClientWorkspaceLinkSchema = z.object({
  workspaceId: link.workspaceId,
  state: link.state,
});
export type IntakeFirmClientWorkspaceLink = z.infer<typeof IntakeFirmClientWorkspaceLinkSchema>;
